import { addDaysToDateString, isRealDate } from '../../utils/warehouseDates'

// Pure calendar math on 'YYYY-MM-DD' strings. No Date is built from the browser's zone.

const daysInMonth = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate()

const pad = value => String(value).padStart(2, '0')

// Same end-of-month clamping as ActiveSupport's `months_since`: Jan 31 + 1 month is Feb 28 (or 29).
export const addMonthsClamped = (dateStr, months) => {
  const [year, month, day] = dateStr.split('-').map(Number)
  const index = year * 12 + (month - 1) + months
  const targetYear = Math.floor(index / 12)
  const targetMonth = (index % 12) + 1
  return `${String(targetYear).padStart(4, '0')}-${pad(targetMonth)}-${pad(Math.min(day, daysInMonth(targetYear, targetMonth)))}`
}

// A period is valid when `to` falls before `from` plus one month.
export const maxToFor = from => addDaysToDateString(addMonthsClamped(from, 1), -1)

// The earliest start whose longest accepted span still reaches `to`.
export const minFromFor = to => {
  const end = addDaysToDateString(to, 1)
  const candidate = addMonthsClamped(end, -1)
  return addMonthsClamped(candidate, 1) < end ? addDaysToDateString(candidate, 1) : candidate
}

const minDate = (a, b) => (a < b ? a : b)

// The whole of a 'YYYY-MM' month, cut at today while the month is still running.
export const monthPeriod = (yearMonth, today) => {
  const [year, month] = yearMonth.split('-').map(Number)
  return { from: `${yearMonth}-01`, to: minDate(`${yearMonth}-${pad(daysInMonth(year, month))}`, today) }
}

const monthOf = date => date.slice(0, 7)

// True for a date in the warehouse's current month or any later one.
export const isCurrentMonthOrLater = (date, today) => monthOf(date) >= monthOf(today)

// Stepping never goes past the warehouse's current month, so a period never ends before it starts.
export const shiftMonth = ({ from }, delta, today) => {
  const month = monthOf(addMonthsClamped(from, delta))
  return monthPeriod(isCurrentMonthOrLater(month, today) ? monthOf(today) : month, today)
}

export const isWholeMonth = ({ from, to }, today) => {
  const month = monthPeriod(from.slice(0, 7), today)
  return month.from === from && month.to === to
}

// The period the screen shows and, when the URL's dates are not real ones, 'malformed'. Which periods are
// acceptable is the platform's rule: a well-formed period is always requested.
export const resolvePeriod = (search, today) => {
  const params = new URLSearchParams(search)
  const from = params.get('from')
  const to = params.get('to')
  const current = monthPeriod(today.slice(0, 7), today)
  if (from === null && to === null) return { period: current, error: null }
  if (!isRealDate(from) || !isRealDate(to)) return { period: current, error: 'malformed' }
  return { period: { from, to }, error: null }
}
